<script setup lang="ts">
  import { ref, onMounted } from 'vue'
  import { APPLE_PAY_SCRIPT_ID } from './ApplePayBtn.constants'
  import { getSupportedNetworks } from '@/shared/utils/ApplePay'

  interface Props {
    price: number
    quoteno: string
    isActive?: boolean
    onBeforePay: () => Promise<void>
  }

  interface Emits {
    (e: 'succeed'): void
    (e: 'error'): void
  }

  const props = withDefaults(defineProps<Props>(), {
    isActive: false,
  })

  const emit = defineEmits<Emits>()

  const isLoaded = ref(false)

  const loadAppleScript = (): Promise<void> =>
    new Promise((resolve) => {
      const el = document.getElementById(APPLE_PAY_SCRIPT_ID)

      if (el) {
        return resolve()
      }

      const script = document.createElement('script')
      script.onload = () => {
        resolve()
      }
      script.async = true
      script.id = APPLE_PAY_SCRIPT_ID
      script.src = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js'
      document.head.appendChild(script)
    })

  const createSession = () => {
    if (!window.ApplePaySession) {
      return
    }

    const request = {
      countryCode: 'QA',
      currencyCode: 'QAR',
      supportedNetworks: getSupportedNetworks(),
      merchantCapabilities: ['supports3DS'],
      total: { label: 'QIC Insurance', amount: String(props.price), type: 'final' },
    }

    return new window.ApplePaySession(3, request)
  }

  const pay = () => {
    if (!window.ApplePaySession) {
      return emit('error')
    }

    const session = createSession()
    if (!session) {
      return emit('error')
    }

    const onBeforePay = props.onBeforePay
    session.orderIdQic = ''
    session.onvalidatemerchant = (event: any) => {
      onBeforePay()
        .then(() => fetch('/payap?action=getorderid'))
        .then((res) => res.json())
        .then((res) => {
          session.orderIdQic = res.orderid
          return fetch(
            `/payap?action=getmerchantsession&url=${event.validationURL}&quoteno=${props.quoteno}`
          )
        })
        .then((res) => res.json())
        .then((merchantSession) => session.completeMerchantValidation(merchantSession))
        .catch((err) => {
          console.error('Error fetching merchant session', err)
          emit('error')
        })
    }

    session.onpaymentmethodselected = () => {
      session.completePaymentMethodSelection({
        newTotal: { label: 'QIC Insurance', amount: String(props.price), type: 'final' },
      })
    }

    session.onpaymentauthorized = (event: any) => {
      const formData = new FormData()
      formData.append('token', JSON.stringify(event.payment.token))
      formData.append('orderid', session.orderIdQic)
      return fetch('/payap?action=paymenttoken', {
        method: 'POST',
        body: formData,
      })
        .then((res) => res.json())
        .then((res) => {
          session.completePayment({ status: res.status })
          if (res.success) {
            emit('succeed')
          } else {
            emit('error')
          }
        })
        .catch((err) => {
          console.log(err.message)
          emit('error')
        })
    }

    session.begin()
  }

  onMounted(() => {
    loadAppleScript().then(() => (isLoaded.value = true))
  })
</script>

<template>
  <div v-if="isLoaded" class="apple-pay">
    <apple-pay-button
      v-show="isActive"
      buttonstyle="black"
      type="pay"
      locale="en"
      @click="pay"
    ></apple-pay-button>
  </div>
</template>

<style lang="scss">
  apple-pay-button {
    --apple-pay-button-max-width: 416px;
    --apple-pay-button-width: 100%;
    --apple-pay-button-height: 80px;
    --apple-pay-button-border-radius: 12px;
    --apple-pay-button-padding: 15px 0px;
    --apple-pay-button-box-sizing: border-box;
  }

  .apple-pay-btn {
    --apple-pay-button-min-height: 80px;
  }

  .apple-pay {
    display: grid;
    direction: ltr !important;
  }

  .apple-pay-btn-wrapper .apple-pay-btn,
  .apple-pay-btn-wrapper {
    height: 80px;
    border-radius: 12px;
  }

  .apple-pay-btn-wrapper .apple-pay-btn > span > svg {
    height: 56px;
  }
</style>
